import React, { useState, useEffect } from 'react';
import {
  Card,
  List,
  Badge,
  Button,
  Space,
  Tag,
  Typography,
  Empty,
  Popconfirm,
  message,
  Dropdown,
  MenuProps
} from 'antd';
import {
  BellOutlined,
  CheckCircleOutlined,
  DeleteOutlined,
  InfoCircleOutlined,
  CheckCircleTwoTone,
  CloseCircleOutlined,
  WarningTwoTone,
  ThunderboltOutlined
} from '@ant-design/icons';
import { notificationAPI } from '../services/api';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

interface Notification {
  _id: string;
  type: string;
  title: string;
  message: string;
  link?: string;
  priority: string;
  isRead: boolean;
  createdAt: string;
}

const Notifications: React.FC = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<'all' | 'unread'>('all');
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    loadNotifications();
    // Her 30 saniyede bir yenile
    const interval = setInterval(loadNotifications, 30000);
    return () => clearInterval(interval);
  }, [filter]);

  const loadNotifications = async () => {
    setLoading(true);
    try {
      const response = await notificationAPI.getNotifications({
        isRead: filter === 'unread' ? 'false' : undefined,
        limit: 100
      });
      setNotifications(response.notifications || []);
      setUnreadCount(response.unreadCount || 0);
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Bildirimler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (id: string) => {
    try {
      await notificationAPI.markNotificationAsRead(id);
      loadNotifications();
    } catch (error: any) {
      message.error('İşlem başarısız');
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationAPI.markAllNotificationsAsRead();
      message.success('Tüm bildirimler okundu olarak işaretlendi');
      loadNotifications();
    } catch (error: any) {
      message.error('İşlem başarısız');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await notificationAPI.deleteNotification(id);
      message.success('Bildirim silindi');
      loadNotifications();
    } catch (error: any) {
      message.error('İşlem başarısız');
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'success':
        return <CheckCircleTwoTone twoToneColor="#52c41a" />;
      case 'error':
        return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
      case 'warning':
        return <WarningTwoTone twoToneColor="#faad14" />;
      case 'price_alert':
      case 'stock_alert':
        return <ThunderboltOutlined style={{ color: '#fa8c16' }} />;
      default:
        return <InfoCircleOutlined style={{ color: '#1890ff' }} />;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'red';
      case 'high':
        return 'orange';
      case 'normal':
        return 'blue';
      default:
        return 'default';
    }
  };

  const filteredNotifications = filter === 'unread'
    ? notifications.filter(n => !n.isRead)
    : notifications;

  return (
    <div>
      <Card styles={{ body: { padding: isMobile ? '12px' : '24px' } }}>
        <div style={{ 
          display: 'flex', 
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between', 
          alignItems: isMobile ? 'flex-start' : 'center', 
          marginBottom: 24,
          gap: isMobile ? '16px' : '0'
        }}>
          <div style={{ width: isMobile ? '100%' : 'auto' }}>
            <Title level={2} style={{ margin: 0, fontSize: isMobile ? '20px' : '24px' }}>
              <BellOutlined /> Bildirimler
            </Title>
            <Text type="secondary" style={{ fontSize: isMobile ? '12px' : '14px' }}>
              {unreadCount > 0 && `${unreadCount} okunmamış bildirim`}
            </Text>
          </div>
          <Space direction={isMobile ? 'vertical' : 'horizontal'} style={{ width: isMobile ? '100%' : 'auto' }} size="small">
            <Button
              onClick={() => setFilter(filter === 'all' ? 'unread' : 'all')}
              block={isMobile}
            >
              {filter === 'all' ? 'Sadece Okunmamışlar' : 'Tümü'}
            </Button>
            {unreadCount > 0 && (
              <Button
                type="primary"
                icon={<CheckCircleOutlined />}
                onClick={handleMarkAllAsRead}
                block={isMobile}
              >
                Tümünü Okundu İşaretle
              </Button>
            )}
          </Space>
        </div>

        {filteredNotifications.length === 0 ? (
          <Empty description="Bildirim bulunmuyor" />
        ) : (
          <List
            loading={loading}
            dataSource={filteredNotifications}
            renderItem={(notification) => (
              <List.Item
                style={{
                  backgroundColor: notification.isRead ? '#fff' : '#f0f7ff',
                  padding: isMobile ? '12px' : '16px',
                  marginBottom: '8px',
                  borderRadius: '8px',
                  border: notification.isRead ? '1px solid #f0f0f0' : '1px solid #1890ff'
                }}
                actions={isMobile ? [] : [
                  !notification.isRead && (
                    <Button
                      type="link"
                      icon={<CheckCircleOutlined />}
                      onClick={() => handleMarkAsRead(notification._id)}
                      size="small"
                    >
                      Okundu
                    </Button>
                  ),
                  <Popconfirm
                    title="Bu bildirimi silmek istediğinizden emin misiniz?"
                    onConfirm={() => handleDelete(notification._id)}
                  >
                    <Button type="link" danger icon={<DeleteOutlined />} size="small" />
                  </Popconfirm>
                ]}
                extra={isMobile ? (
                  <Space direction="vertical" size="small">
                    {!notification.isRead && (
                      <Button
                        type="link"
                        icon={<CheckCircleOutlined />}
                        onClick={() => handleMarkAsRead(notification._id)}
                        size="small"
                        style={{ padding: 0 }}
                      >
                        Okundu
                      </Button>
                    )}
                    <Popconfirm
                      title="Bu bildirimi silmek istediğinizden emin misiniz?"
                      onConfirm={() => handleDelete(notification._id)}
                    >
                      <Button type="link" danger icon={<DeleteOutlined />} size="small" style={{ padding: 0 }} />
                    </Popconfirm>
                  </Space>
                ) : null}
              >
                <List.Item.Meta
                  avatar={
                    <Badge dot={!notification.isRead}>
                      {getTypeIcon(notification.type)}
                    </Badge>
                  }
                  title={
                    <Space>
                      <Text strong={!notification.isRead}>{notification.title}</Text>
                      <Tag color={getPriorityColor(notification.priority)}>
                        {notification.priority === 'urgent' ? 'Acil' :
                         notification.priority === 'high' ? 'Yüksek' :
                         notification.priority === 'normal' ? 'Normal' : 'Düşük'}
                      </Tag>
                    </Space>
                  }
                  description={
                    <div>
                      <Text>{notification.message}</Text>
                      <br />
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        {dayjs(notification.createdAt).format('DD MMMM YYYY, HH:mm')}
                      </Text>
                    </div>
                  }
                />
              </List.Item>
            )}
          />
        )}
      </Card>
    </div>
  );
};

export default Notifications;

